import {jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {MainLayout} from '~/layouts/main';
import {Figure} from '~/components/Figure';
import {Quiz} from '~/components/Quiz';
export const meta = {
  title: 'LSP | Антипаттерны и запахи',
  description: 'Антипаттерны и запахи кода, которые мешают следовать принципу подстановки Барбары Лисков'
};
const MDXLayout = ({children}) => _jsx(MainLayout, {
  meta: meta,
  children: children
});
function _createMdxContent(props) {
  const _components = {
    a: "a",
    code: "code",
    em: "em",
    h1: "h1",
    h2: "h2",
    li: "li",
    p: "p",
    pre: "pre",
    section: "section",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  return _jsxs(_components.section, {
    children: [_jsx(_components.h1, {
      children: "Антипаттерны и запахи"
    }), _jsx(_components.p, {
      children: "Неправильная или неполная реализация некоторых шаблонов проектирования или приёмов, а также неправильная иерархия сущностей могут нарушить LSP."
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Непредсказуемое изменение поведения"
      }), _jsx(_components.p, {
        children: "Допустим, мы делаем клон Медиума, где авторы будут публиковать статьи. Статья может находиться в разных состояниях, от которых зависит, что с ней можно делать. Например, удалённую статью нельзя удалить, а опубликованную — снова опубликовать."
      }), _jsxs(Figure, {
        children: [_jsx("img", {
          src: "/lsp-violation.svg"
        }), _jsx("figcaption", {
          children: "Схема доступных действий со статьёй в зависимости от её состояния"
        })]
      }), _jsxs(_components.p, {
        children: ["Для подобной задачи подходит шаблон ", _jsx(_components.a, {
          href: "https://refactoring.guru/ru/design-patterns/state",
          children: "Состояние"
        }), " — он позволяет менять поведение объектов в зависимости от их внутреннего состояния. Если он реализован правильно и полно, то LSP он не нарушит. Однако реализация в примере ниже нарушает."]
      }), _jsxs(_components.p, {
        children: ["Допустим, статья описывается базовым классом ", _jsx(_components.code, {
          children: "Article"
        }), ":"]
      }), _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "language-ts",
          children: "enum ArticleStatus {\n  Draft\n  Published\n  Deleted\n}\n\nclass Article {\n  status: ArticleStatus\n\n  constructor() {/*...*/}\n  edit() {/*...*/}\n  delete() {/*...*/}\n  restore() {/*...*/}\n  unpublish() {/*...*/}\n\n  publish(): void {\n    this.status = ArticleStatus.Published\n  }\n}\n"
        })
      }), _jsxs(_components.p, {
        children: ["Если опубликованная статья при попытке публикации выбрасывает исключение, ", _jsx(_components.em, {
          children: "которое не было описано в базовом классе"
        }), ", то ", _jsx(_components.a, {
          href: "https://softwareengineering.stackexchange.com/questions/170138/is-this-a-violation-of-the-liskov-substitution-principle",
          children: "мы нарушаем LSP"
        }), ":"]
      }), _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "language-ts",
          children: "class Published extends Article {\n  constructor() {\n    super({ status: ArticleStatus.Published })\n  }\n\n  publish(): void {\n    // Упс!\n    throw new Error('article is already published')\n  }\n}\n"
        })
      }), _jsx(_components.p, {
        children: "Чтобы реализация шаблона не нарушала LSP, нам необходимо описать в базовом классе возможность выбросить исключение. Для этого мы введём метод, который будет определять, можно ли статью публиковать:"
      }), _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "language-ts",
          children: "class ArticleException extends Error {/*...*/}\n\nclass Article {\n  // ...\n\n  protected canPublish(): boolean {\n    return this.status === ArticleStatus.Draft\n  }\n\n  publish(): void {\n    if (!this.canPublish()) throw new ArticleException()\n    // ...\n  }\n}\n"
        })
      }), _jsxs(_components.p, {
        children: ["Сейчас переопределение метода ", _jsx(_components.code, {
          children: "publish"
        }), " для опубликованной статьи не будет усиливать предусловия, поэтому это не нарушит LSP:"]
      }), _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "language-ts",
          children: "class ArticlePublishedException extends ArticleException {/*...*/}\n\nclass Published extends Article {\n  // ...\n\n  publish(): void {\n    // `ArticlePublishedException` наследуется от `ArticleException`,\n    // указанного в классе `Article`, поэтому здесь нарушения нет:\n\n    throw new ArticlePublishedException()\n  }\n}\n"
        })
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Вопросы"
      }), _jsx(Quiz, {
        name: "lsp-anti-1"
      }), _jsx(Quiz, {
        name: "lsp-anti-2"
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Интерфейс, которому нельзя доверять"
      }), _jsxs(_components.p, {
        children: ["Более тонкое нарушение LSP — это ", _jsx(_components.a, {
          href: "https://stackoverflow.com/questions/20861107/can-anyone-provide-an-example-of-the-liskov-substitution-principle-lsp-using-v",
          children: "«пустая» реализация интерфейса"
        }), "."]
      }), _jsxs(_components.p, {
        children: ["Если опереться на пример выше, то пустой реализацией было бы описание метода ", _jsx(_components.code, {
          children: "Publish"
        }), " для опубликованной статьи таким образом:"]
      }), _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "language-ts",
          children: "class Published extends Article {\n  // ...\n\n  publish(): void {\n    return\n  }\n}\n"
        })
      }), _jsxs(_components.p, {
        children: ["Вроде всё хорошо: метод описывает правильное поведение, усиления предусловия нет. Но если посмотреть на ситуацию в терминах ", _jsx(_components.a, {
          href: "/lsp/patterns",
          children: "контрактного программирования"
        }), ", то метод ", _jsx(_components.code, {
          children: "publish"
        }), " должен менять состояние статьи с ", _jsx(_components.code, {
          children: "Draft"
        }), " на ", _jsx(_components.code, {
          children: "Published"
        }), ", чего не будет происходить:"]
      }), _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "language-ts",
          children: "class Article {\n  // ...\n\n  publish(): void {\n    // Проверяем, что состояние позволяет публиковать статью\n    // именно эта проверка в `Published` вызовет ошибку:\n\n    this.contract.require(this.canPublish() === true)\n\n    // ...\n\n    // Проверяем, что состояние поменялось на `Published`:\n    this.contract.ensure(this.status === ArticleStatus.Published)\n  }\n}\n"
        })
      }), _jsxs(_components.p, {
        children: ["«Пустая» реализация интерфейса также нарушает ", _jsx(_components.a, {
          href: "/isp",
          children: "принцип разделения интерфейса"
        }), "."]
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Вопросы"
      }), _jsx(Quiz, {
        name: "lsp-anti-3"
      }), _jsx(Quiz, {
        name: "lsp-anti-4"
      })]
    }), _jsxs(_components.section, {
      children: [_jsx(_components.h2, {
        children: "Материалы к разделу"
      }), _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://refactoring.guru/ru/design-patterns/state",
            children: "Состояние"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://softwareengineering.stackexchange.com/questions/181922/does-the-state-pattern-violate-liskov-substitution-principle",
            children: "Does the state pattern violate LSP?"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.codeproject.com/Articles/648987/Violating-Liskov-Substitution-Principle-LSP",
            children: "Violating LSP"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://stackoverflow.com/questions/20861107/can-anyone-provide-an-example-of-the-liskov-substitution-principle-lsp-using-v",
            children: "Examples of LSP violation"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://medium.com/@ipapikas/solid-series-3-5-liskov-substitution-principle-c71500ce9d29",
            children: "Common pitfalls and bad practices"
          })
        }), "\n"]
      })]
    })]
  });
}
export default function MDXContent(props = {}) {
  return _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  });
}
